import { Button, ButtonGroup } from "@components/button";
import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { formatHeaderValue } from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { getCollapsedGroupId } from "@components/smart/Smart.utils";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { ISummaryItemProps } from "@components/summary/SummaryItem";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    AddressEntity,
    EntitySetName,
    EntityTypeName,
    IPrAttendanceEntity,
    IPrEmploymentEntity,
    IPrEmploymentExtraSalaryEntity,
    IPrPayrollPeriodEntity,
    IPrSalaryComponentEntity,
    PrAttendanceEntity,
    PrEmploymentEntity,
    PrEmploymentInitialDataEntity,
    PrEmploymentTemporalCurrentEntity,
    PrInitialDataEntity,
    PrSalaryComponentEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    PrAttendanceStatusCode,
    PrEmploymentCategoryCode,
    PrEmploymentTypeCode,
    PrEmploymentWorkplaceAddressSelectionCode,
    PrExtraSalaryOriginCode,
    PrPayrollPeriodStatusCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentTypeCode,
    PrSubmissionSubtypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { setTemporalCurrentValue } from "@odata/TemporalUtils";
import { EXPORT_CERTIFICATE_ACTION } from "@pages/payroll/employment/EmploymentDef";
import EmploymentInitialDataDialog from "@pages/payroll/employment/initialData/EmploymentInitialDataDialog";
import TerminateEmploymentDialog from "@pages/payroll/employment/TerminateEmploymentDialog";
import { createDateFromPeriod, fetchPayrollPeriods } from "@pages/payroll/payroll.utils";
import { setupRetirementSavingsCompanyId } from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import SubmissionWizard from "@pages/payroll/submission/submissionWizard/SubmissionWizard";
import FileStorage from "@utils/FileStorage";
import { isDefined, isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { universalPersonNameFormatter } from "@utils/string";
import { Dayjs } from "dayjs";
import saveAs from "file-saver";
import React from "react";

import { REST_API_URL } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { addCompanyIdToUrl } from "../../../contexts/appContext/AppContext.utils";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { Status } from "../../../enums";
import DateType, { DATE_MAX, getUtcDayjs } from "../../../types/Date";
import { FormStorage, IContextInitArgs, IFormStorageSaveResult, ISaveArgs } from "../../../views/formView/FormStorage";
import { IFormViewProps } from "../../../views/formView/FormView";
import { userNameWithAvatarFormatter } from "../../admin/users/Users.utils";
import EmploymentBaseFormView, { IEmploymentFormCustomData } from "../employmentTemplate/EmploymentBaseFormView";
import {
    createDefaultLegislativeExtraSalaryItems,
    fetchLastClosedAttendance,
    getCsszEmploymentTypesForSelectedType,
    getEmploymentCSSZSelectItems,
    initiateWithTemplate
} from "./Employment.utils";
import {
    EmployeeInitialDataPath,
    EmploymentInitialDataActionPath,
    isInitialDataRelevantEmployment
} from "./initialData/EmploymentInitialData.utils";

export interface IInitialDataEmploymentValidation {
    employeeId: number;
    otherEmployments: IPrEmploymentEntity[];
    hasCalculatedPayroll: boolean;
    shouldValidate: boolean;
}

export interface IEmploymentCustomData extends IEmploymentFormCustomData {
    initialDataDialogOpen?: boolean;
    initialDataBoundaries?: {
        DateStart?: Date;
        DateCalculatePayslipStart?: Date;
    };
    lastClosedPayrollPeriod: {
        Month: number;
        Year: number;
    };
    lastClosedAttendanceDate?: Dayjs;
    initialDataEmploymentValidation?: IInitialDataEmploymentValidation;
}

interface IProps extends IFormViewProps<IPrEmploymentEntity, IEmploymentCustomData>, WithConfirmationDialog {

}

const EXPORT_CERTIFICATE_URL = `${REST_API_URL}/PrEmploymentCertificatePdfExport`;

class EmploymentFormView extends EmploymentBaseFormView<IProps> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Employment:FormTitle"
    };

    onConfirmInitialData = async (confirmedData: IEntity): Promise<void> => {
        const { storage } = this.props;
        const initialDataEntity = confirmedData as IPrEmploymentEntity;
        const initialDataYear = storage.data.entity.DateCalculatePayslipStart?.getFullYear();

        storage.setValueByPath(PrEmploymentEntity.InitialData, {
            ...initialDataEntity.InitialData,
            [PrEmploymentInitialDataEntity.Year]: initialDataEntity.InitialData?.Year ?? initialDataYear
        });
        this.storeInitialDataBoundaries();

        // the dialog removes employee initial data from the payload when they are unchanged
        const employeeInitialData = initialDataEntity.Employee?.InitialData;
        if (employeeInitialData) {
            storage.setValueByPath(EmployeeInitialDataPath, {
                ...employeeInitialData,
                [PrInitialDataEntity.Year]: employeeInitialData.Year ?? initialDataYear
            });
        } else {
            this.clearPendingEmployeeInitialData();
        }

        await storage.validateFields([
            storage.data.bindingContext.navigate(EmploymentInitialDataActionPath)
        ]);
        storage.refresh();
        this.closeInitialDataDialog();
    };

    getAdditionalLoadPromise(args: IContextInitArgs): (Promise<unknown | void>)[] {
        return [
            fetchPayrollPeriods(this.props.storage.oData, { status: [PrPayrollPeriodStatusCode.Processed, PrPayrollPeriodStatusCode.ProcessedAndClosed] }),
            fetchLastClosedAttendance(this.props.storage),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    onAfterLoad(): Promise<void> {
        this.setLegalAddressAsWorkplaceAddressIfNeeded();
        const periods = this.getAdditionalResults()[0] as IPrPayrollPeriodEntity[];
        const lastClosedPeriod = periods?.[periods?.length - 1];
        if (lastClosedPeriod) {
            this.props.storage.setCustomData({
                lastClosedPayrollPeriod: {
                    Month: lastClosedPeriod.Month,
                    Year: lastClosedPeriod.Year
                }
            });
        }

        const lastClosedAttendance = this.getAdditionalResults()[1] as IPrAttendanceEntity;
        this.props.storage.setCustomData({
            lastClosedAttendanceDate: lastClosedAttendance
                ? getUtcDayjs(createDateFromPeriod(lastClosedAttendance)).endOf("month")
                : undefined
        });

        setupRetirementSavingsCompanyId(this.props.storage.data.entity);
        this.storeInitialDataBoundaries();

        return super.onAfterLoad();
    }

    onBeforeSave(): IEntity {
        const entity = super.onBeforeSave() as IPrEmploymentEntity;

        if (entity.WorkplaceAddressSelectionCode === PrEmploymentWorkplaceAddressSelectionCode.CompanyDefault) {
            entity.WorkplaceAddress = null;
        }

        if (entity.TypeCode === PrEmploymentTypeCode.Compensation) {
            entity.ExtraSalaries = null;
            entity.CurrentTemporalPropertyBag.WorkingPattern = null;
            entity.TemporalPropertyBag.forEach(tb => {
                tb.WorkingPattern = null;
            });
            // todo: group ranges as some might have been merged
        }

        return entity;
    }

    exportEmploymentCertificate: () => Promise<void> = async () => {
        const { storage } = this.props;
        try {
            const url = addCompanyIdToUrl(`${EXPORT_CERTIFICATE_URL}?employmentIds=${this.entity.Id}`, storage.context);
            const file = await FileStorage.get(null, { url });

            saveAs(file);
        } catch (error) {
            logger.error("Error while exporting employment certificate", error);
        }
    };

    async save(saveArgs?: Partial<ISaveArgs>): Promise<IFormStorageSaveResult> {
        const { storage } = this.props;
        try {
            await this.loadInitialDataEmploymentValidation();
        } catch (error) {
            logger.error("Failed to load employment initial data validation", error);
            storage.setFormAlert({
                status: Status.Error,
                title: storage.t("Employment:Form.InitialDataValidationUnavailable")
            });
            return null;
        }

        const updateEnabledFor = storage.data.entity.Employee?.InitialData
            ? [...(saveArgs?.updateEnabledFor ?? []), PrEmploymentEntity.Employee]
            : saveArgs?.updateEnabledFor;

        const result = await super.save({
            ...saveArgs,
            updateEnabledFor
        });

        if (!result) {
            this.updateInitialDataValidationAlert();
        }

        return result;
    }

    updateInitialDataValidationAlert(): void {
        const { storage } = this.props;
        const alert = storage.data.alert;
        const isInitialDataError = EmploymentInitialDataActionPath in alert.detailData;
        if (!alert?.detailData || !(isInitialDataError)) {
            return;
        }

        const { [EmploymentInitialDataActionPath]: initialDataError, ...otherErrors } = alert.detailData;
        const message = storage.t("Employee:InitialData.MissingBeforeSave");
        const defaultSubtitle = storage.t("Common:General.FormValidationErrorSubTitle");
        const subtitles = Array.isArray(alert.subTitle) ? alert.subTitle : [alert.subTitle];
        const otherSubtitles = subtitles.filter(subtitle => subtitle && subtitle !== defaultSubtitle && subtitle !== message);

        storage.setFormAlert({
            ...alert,
            subTitle: [message, ...otherSubtitles],
            detailData: {
                ...otherErrors,
                [storage.t("Employee:InitialData.Title")]: initialDataError
            }
        });
        storage.refresh();
    }

    async loadInitialDataEmploymentValidation(): Promise<void> {
        const { storage } = this.props;
        const employment = storage.data.entity;
        const original = storage.data.origEntity;
        const employeeId = employment.Employee?.Id;
        const shouldValidate = storage.data.bindingContext.isNew()
            || employeeId !== original?.Employee?.Id
            || !DateType.isSame(employment.DateStart, original?.DateStart)
            || !DateType.isSame(employment.DateCalculatePayslipStart, original?.DateCalculatePayslipStart);

        let otherEmployments: IPrEmploymentEntity[] = [];
        let hasCalculatedPayroll = false;

        if (employeeId && shouldValidate) {
            const employmentQuery = isInitialDataRelevantEmployment(employment)
                ? storage.oData.getEntitySetWrapper(EntitySetName.PrEmployments).query()
                    .filter(`${PrEmploymentEntity.Employee}/Id eq ${employeeId}`)
                    .select(PrEmploymentEntity.Id, PrEmploymentEntity.DateCalculatePayslipStart)
                    .expand(PrEmploymentEntity.InitialData, query => query.select("Id"))
                    .fetchData<IPrEmploymentEntity[]>()
                : Promise.resolve(null);

            const attendanceQuery = employment.DateStart && employment.DateCalculatePayslipStart
                && !getUtcDayjs(employment.DateStart).isSame(employment.DateCalculatePayslipStart, "month")
                ? storage.oData.getEntitySetWrapper(EntitySetName.PrAttendances).query()
                    .filter(`${createPath(PrAttendanceEntity.Employment, PrEmploymentEntity.Employee, "Id")} eq ${employeeId} AND ${PrAttendanceEntity.StatusCode} in ('${PrAttendanceStatusCode.Locked}', '${PrAttendanceStatusCode.Processed}')`)
                    .select(PrAttendanceEntity.Id)
                    .top(1)
                    .fetchData<IPrAttendanceEntity[]>()
                : Promise.resolve(null);

            const [employmentsResult, attendancesResult] = await Promise.all([employmentQuery, attendanceQuery]);
            otherEmployments = employmentsResult?.value?.filter((other: IPrEmploymentEntity) => other.Id !== employment.Id) ?? [];
            hasCalculatedPayroll = !!attendancesResult?.value?.length;
        }

        storage.setCustomData({
            initialDataEmploymentValidation: { employeeId, otherEmployments, hasCalculatedPayroll, shouldValidate }
        });
    }

    clearPendingEmployeeInitialData(): void {
        if (this.props.storage.data.entity.Employee) {
            delete this.props.storage.data.entity.Employee.InitialData;
        }
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean): void {
        this.clearPendingEmployeeInitialData();
        super.onAfterSave(isNew, preventTableViewRefresh);
    }

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        switch (actionId) {
            case PrSubmissionTypeCode.EmployeeRegistration:
                this.props.storage.setCustomData({ submissionType: actionId });
                this.forceUpdate();
                break;
            case EXPORT_CERTIFICATE_ACTION:
                await this.exportEmploymentCertificate();
                break;
            default:
                return super.handleCustomHeaderAction(actionId);
        }
    }

    getHeaderData = (): IReadOnlyListItem[] => {
        const { storage } = this.props;
        const { bindingContext, entity } = storage.data;

        return [
            {
                label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Employee)).label,
                value: formatHeaderValue(universalPersonNameFormatter(entity.Employee))
            },
            {
                label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Type)).label,
                value: formatHeaderValue(entity.Type?.Name ?? getEnumDisplayValue(EntityTypeName.PrEmploymentType, entity.TypeCode ?? entity.Type?.Code))
            },
            // todo: ... there will be Name property on the EmploymentEntity in the future
            // {
            //
            //     label: storage.getInfo(bindingContext.navigate(PrEmploymentEntity.Name)).label,
            //     value: formatHeaderValue(entity.Name)
            // }
        ];
    };

    get readOnlyListTitle(): string {
        return this.props.storage.t("Employment:FormTitle");
    }

    handleTypeChange = async (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === PrEmploymentEntity.Type) {
            const { storage } = this.props;
            const type = e.value as PrEmploymentTypeCode;
            // find CSSZ codes available for the selected type
            const allItems = await getEmploymentCSSZSelectItems(storage);
            const availableItems = getCsszEmploymentTypesForSelectedType(allItems, type);
            // if there are some items, set the first one as default, otherwise clear the field
            if (!availableItems?.length) {
                storage.clearValueByPath(PrEmploymentEntity.CsszEmploymentType);
            } else {
                storage.setValueByPath(PrEmploymentEntity.CsszEmploymentType, availableItems?.[0]?.id);
            }

            const isDPP = type === PrEmploymentTypeCode.WorkAgreementDPP;
            const isCompensation = type === PrEmploymentTypeCode.Compensation;
            if (isDPP || isCompensation) {
                // switch regular salary component to hourly if present
                const componentsBc = storage.data.bindingContext.navigate(PrEmploymentEntity.SalaryComponents);
                const salaryComponents = storage.getValue(componentsBc) as IPrSalaryComponentEntity[];
                salaryComponents?.forEach((component, idx) => {
                    const componentType = component.TypeCode ?? component?.Type?.Code;
                    if (componentType === PrSalaryComponentTypeCode.Regular) {
                        const bc = componentsBc.addKey(component).navigate(PrSalaryComponentEntity.RegularSalaryType);
                        if (isDPP && component.RegularSalaryTypeCode !== PrRegularSalaryTypeCode.Hourly) {
                            storage.setValue(bc, PrRegularSalaryTypeCode.Hourly);
                        }
                        if (isCompensation && component.RegularSalaryTypeCode !== PrRegularSalaryTypeCode.Monthly) {
                            storage.setValue(bc, PrRegularSalaryTypeCode.Monthly);
                        }
                    }
                });
            }
            if (isDPP) {
                const isSmallScaleBc = storage.data.bindingContext
                    .navigate(PrEmploymentEntity.CurrentTemporalPropertyBag)
                    .navigate(PrEmploymentTemporalCurrentEntity.IsSmallScaleEmployment);
                setTemporalCurrentValue(storage, isSmallScaleBc, false);
            }

            this.entity.ExtraSalaries = this.entity.ExtraSalaries.filter((es: IPrEmploymentExtraSalaryEntity) => es.Default?.Type?.OriginCode !== PrExtraSalaryOriginCode.Legislative);
            await createDefaultLegislativeExtraSalaryItems(storage);
            storage.refresh();
        }
    }

    handleEmploymentCategoryChange = (e: ISmartFieldChange) => {
        if (e.bindingContext.getPath() === PrEmploymentEntity.EmploymentCategory) {
            const { storage } = this.props;
            if (storage.getValueByPath(PrEmploymentEntity.TypeCode) === PrEmploymentTypeCode.Compensation && e.value === PrEmploymentCategoryCode.Employee) {
                storage.clearValueByPath(PrEmploymentEntity.Type);
                storage.clearValueByPath(PrEmploymentEntity.TypeCode);
                storage.refreshFields();
            }
        }
    };

    async handleTemplateChange(e: ISmartFieldChange) {
        if (e.bindingContext.getPath() === PrEmploymentEntity.Template) {
            await initiateWithTemplate(this.props.storage, e.value as string);
            this.expandCurrentTemporalBagsFromDefault();
        }
    }

    setLegalAddressAsWorkplaceAddressIfNeeded(force?: boolean): void {
        const { storage } = this.props;
        const employment = storage.data.entity;
        const workplaceAddressSelection = employment.WorkplaceAddressSelectionCode;
        if (force || workplaceAddressSelection === PrEmploymentWorkplaceAddressSelectionCode.CompanyDefault) {
            const company = storage.context.getCompany();
            const legalAddress = company.LegalAddress;
            storage.setValueByPath(PrEmploymentEntity.WorkplaceAddress, legalAddress);
        }
    }

    handleWorkplaceAddressSelectionChange(e: ISmartFieldChange): void {
        if (e.bindingContext.getPath() === PrEmploymentEntity.WorkplaceAddressSelection) {
            const { storage } = this.props;
            const selection = e.value as string;
            storage.clearValueByPath(PrEmploymentEntity.WorkplaceAddress);
            const isOwn = selection === PrEmploymentWorkplaceAddressSelectionCode.Own;
            if (isOwn) {
                storage.setValueByPath(createPath(PrEmploymentEntity.WorkplaceAddress, AddressEntity.Country), CountryCode.CzechRepublic);
            } else {
                this.setLegalAddressAsWorkplaceAddressIfNeeded(true);
            }
            storage.setGroupStatus({
                isExpanded: isOwn
            }, getCollapsedGroupId(PrEmploymentEntity.WorkplaceAddressSelection));
            storage.refresh(true);
        }
    }

    handleFirstDayAtWorkDefaulting(e: ISmartFieldChange): void {
        const { storage } = this.props;
        if (e.bindingContext.getPath() === PrEmploymentEntity.DateStart && storage.data.bindingContext.isNew()) {
            // actually we are checking DateStart change and updating DateFirstDayAtWork accordingly
            const { DateFirstDayAtWork, DateStart } = storage.data.entity;
            const current = DateFirstDayAtWork && getUtcDayjs(DateFirstDayAtWork);
            const previous = DateStart && getUtcDayjs(DateStart);
            if (!current || current.isSame(previous, "day")) {
                storage.setValueByPath(PrEmploymentEntity.DateFirstDayAtWork, DateType.isValid(e.value as Date) ? e.value : null);
            }
        }
    }

    isInitialDataBoundary(path: string): boolean {
        return path === PrEmploymentEntity.DateStart || path === PrEmploymentEntity.DateCalculatePayslipStart;
    }

    getInitialDataBoundaryValue(path: string): Date | undefined {
        const { initialDataBoundaries } = this.props.storage.getCustomData();
        if (path === PrEmploymentEntity.DateStart) {
            return initialDataBoundaries?.DateStart;
        }
        if (path === PrEmploymentEntity.DateCalculatePayslipStart) {
            return initialDataBoundaries?.DateCalculatePayslipStart;
        }
        return undefined;
    }

    storeInitialDataBoundaries(): void {
        const { storage } = this.props;
        if (!storage.data.entity.InitialData) {
            return;
        }

        storage.setCustomData({
            initialDataBoundaries: {
                DateStart: storage.data.entity.DateStart,
                DateCalculatePayslipStart: storage.data.entity.DateCalculatePayslipStart
            }
        });
    }

    async confirmInitialDataInvalidation(): Promise<boolean> {
        const { storage } = this.props;
        return this.props.confirmationDialog.open({
            content: storage.t("Employment:Form.InitialDataInvalidationConfirmation")
        });
    }

    async handleInitialDataInvalidation(): Promise<void> {
        const { storage } = this.props;
        storage.clearValueByPath(PrEmploymentEntity.InitialData);
        storage.setCustomData({ initialDataBoundaries: undefined });
        storage.refresh();
    }

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        await super.handleBlur(args);

        const { storage } = this.props;
        const path = args.bindingContext.getPath();
        const previousValue = this.getInitialDataBoundaryValue(path);
        const currentValue = storage.getValue(args.bindingContext) as Date;
        if (!args.wasChanged
            || !this.isInitialDataBoundary(path)
            || !storage.data.entity.InitialData
            || !previousValue
            || DateType.isSame(currentValue, previousValue)) {
            return;
        }

        if (await this.confirmInitialDataInvalidation()) {
            await this.handleInitialDataInvalidation();
        } else {
            storage.setValueByPath(path, previousValue);
            storage.refresh();
        }
    }

    async handleChange(e: ISmartFieldChange): Promise<void> {
        // keep before storage.handleChange to have access to previous values if needed
        this.handleFirstDayAtWorkDefaulting(e);
        this.handleWorkplaceAddressSelectionChange(e);

        super.handleChange(e);
        this.handleTypeChange(e);
        this.handleEmploymentCategoryChange(e);
        await this.handleTemplateChange(e);
    }

    get submissionDefaultData(): IEntity {
        const { storage } = this.props;
        const {
            Employee: {
                FirstName,
                LastName,
                Id: EmployeeId
            },
            Id, NumberOurs
        } = storage.data.entity;
        return {
            Employee: {
                Employee: {
                    Id: EmployeeId, FirstName, LastName
                },
                FirstName,
                LastName
            },
            Employment: {
                Employment: {
                    Id, NumberOurs
                }
            }
        };
    }

    get submissionSubtype(): PrSubmissionSubtypeCode {
        return null;
    }

    get isEndedEmployment(): boolean {
        return !isObjectEmpty((this.entity as IPrEmploymentEntity)?.EmploymentClose);
    }

    /* Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */
    closeSubmissionDialog = (): void => {
        this.props.storage.setCustomData({ submissionType: null });
        this.forceUpdate();
    };

    confirmSubmissionDialog = (): void => {
        const { storage } = this.props;
        // Submission saved -> reload the form
        // todo: reload only the table?
        storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
        // close the dialog
        this.closeSubmissionDialog();
    };

    getSubmissionSummaryItems(): ISummaryItemProps[] {
        const storage = this.props.storage as FormStorage<IPrEmploymentEntity>;
        const entity = storage.data.origEntity;
        const name = entity.Employee.FirstName;
        const formatted = userNameWithAvatarFormatter(name, { storage, entity: entity.Employee }, {
            isBig: true
        });

        return [
            {
                id: BindingContext.localContext(PrEmploymentEntity.Employee),
                label: null, // wo label
                value: typeof formatted === "string" ? formatted : formatted.value,
            },
            {
                id: BindingContext.localContext(PrEmploymentEntity.NumberOurs),
                label: storage.t("Employment:FormTitle"),
                value: entity.NumberOurs
            }
        ];
    }

    renderSubmissionDialog = () => {
        const { storage } = this.props;
        const { submissionType } = storage.getCustomData();
        if (submissionType) {
            return (
                <SubmissionWizard generatorKey={storage.data.bindingContext?.getKey()}
                                  onCancel={this.closeSubmissionDialog}
                                  onConfirm={this.confirmSubmissionDialog}
                                  defaultData={this.submissionDefaultData}
                                  summaryItems={this.getSubmissionSummaryItems()}
                                  mainType={submissionType}
                                  subType={this.submissionSubtype}/>
            );
        }
        return null;
    };

    /* /end/ Shared code between EmployeeFormView, SocialEventFormView and EmploymentFormView */

    handleEndEmployment = async (): Promise<void> => {
        const { storage } = this.props;
        storage.setCustomData({
            isEndingEmployment: true
        });
        this.forceUpdate();
    };

    handleReopenEmployment = async (): Promise<void> => {
        const { storage } = this.props;
        const res = await storage.save({
            data: {
                [PrEmploymentEntity.EmploymentClose]: null,
                [PrEmploymentEntity.DateEnd]: DATE_MAX
            }
        });
        if (!res) {
            return;
        }
        await storage.reload({ preserveInfos: true, preserveData: true, withoutBusy: true });
    };

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;
        const isNewEmployment = storage.data.bindingContext.isNew();

        const entity = storage.data.entity as IPrEmploymentEntity;
        const lastClosedPeriod = storage.getCustomData()?.lastClosedPayrollPeriod;
        let lastPaycheckIsProcessed: boolean;
        if (!lastClosedPeriod) {
            lastPaycheckIsProcessed = false;
        } else {
            const lastClosedPeriodDate = getUtcDayjs(createDateFromPeriod(lastClosedPeriod)).endOf("month");
            lastPaycheckIsProcessed = entity.DateEnd && getUtcDayjs(entity.DateEnd).isSameOrBefore(lastClosedPeriodDate, "day");
        }

        if (this.isEndedEmployment) {
            if (lastPaycheckIsProcessed) {
                return null;
            }

            return <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"reopenEmployment"}
                        onClick={this.handleReopenEmployment}
                        isTransparent>{storage.t("Employment:Form.ReopenEmployment")}</Button>
            </ButtonGroup>;
        }

        return (
            <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                {this.isDeletable &&
                    <SmartFormDeleteButton storage={storage}
                                           onClick={this.handleDelete}/>
                }
                {!this.isEndedEmployment && !isNewEmployment && <Button hotspotId={"endEmployment"}
                                                    isDisabled={storage.isDisabled}
                                                    onClick={this.handleEndEmployment}
                                                    isTransparent>{storage.t("Employment:Form.EndEmployment")}</Button>}
                {this.shouldAllowCopy() && <Button hotspotId={"formCopy"}
                                                   onClick={this.handleCopy}
                                                   isDisabled={this.shouldDisableCopyButton()}
                                                   isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>}
                {!isFormReadOnly &&
                    <Button hotspotId={"formSave"}
                            onClick={this.handleSaveClick}
                            isDisabled={this.shouldDisableSaveButton()}>
                        {storage.t("Common:General.Save")}
                    </Button>
                }
            </ButtonGroup>
        );
    }

    closeEndEmploymentDialog = (): void => {
        this.props.storage.setCustomData({ isEndingEmployment: false });
        this.forceUpdate();
    };

    confirmEndEmploymentDialog = async (): Promise<void> => {
        const { storage } = this.props;
        await storage.reload({ preserveInfos: true, preserveData: false, withoutBusy: false });
        this.closeEndEmploymentDialog();
    };

    renderEndEmploymentDialog = () => {
        const { storage } = this.props;
        const { isEndingEmployment } = storage.getCustomData();
        if (isEndingEmployment) {
            return (
                <TerminateEmploymentDialog onCancel={this.closeEndEmploymentDialog}
                                           bindingContext={storage.data.bindingContext}
                                           onConfirm={this.confirmEndEmploymentDialog}/>
            );
        }
        return null;
    };

    closeInitialDataDialog = (): void => {
        this.props.storage.setCustomData({ initialDataDialogOpen: false });
        this.forceUpdate();
    };

    renderInitialDataDialog = (): React.ReactElement | null => {
        const { storage } = this.props;
        if (!storage.getCustomData().initialDataDialogOpen) {
            return null;
        }

        return <EmploymentInitialDataDialog
            bindingContext={storage.data.bindingContext}
            employment={storage.data.entity}
            isReadOnly={isDefined(storage.getCustomData().lastClosedAttendanceDate)}
            onClose={this.closeInitialDataDialog}
            onConfirm={this.onConfirmInitialData}/>;
    };


    renderCustomDialogs(): React.ReactElement {
        return (
            <>
                {super.renderCustomDialogs()}
                {this.renderSubmissionDialog()}
                {this.renderEndEmploymentDialog()}
                {this.renderInitialDataDialog()}
            </>);
    }
}

export default withConfirmationDialog(withPermissionContext(EmploymentFormView));
